/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableCell,
  TableHead,
  Input,
} from '@leadsnavi/ui-kit';
import { DateRangePicker } from '@leadsnavi/ui-kit';

import React, { useEffect, useMemo, useRef, useState } from 'react';
import ReactECharts from 'echarts-for-react';
import * as echarts from 'echarts';
import { useRequest } from 'ahooks';

import { ExportAnalyze as ExportAnalyzeType } from '../types';
import { StatMiniCard } from './stat-mimi-card';
import { BuyersMapComponent } from './buyers-map';
import { Calendar, DollarSign, Users } from 'lucide-react';
import { RecordList } from './record-list';
import {
  getBuyerMapList,
  getSuppliersBase,
  getSuppliersStatistics,
  getSupplierTransctionRecordList,
  getTimeLine,
} from '../../services';
import { IApiCompanyCountryVO, IApiCompanyListRecordReq, IApiLineChartVO } from '@/services/swagger/customs/models';
import { IApiEaseSourcingCompanyDetailResultVO } from '@/services/swagger/globalSearch/models';
import BasePagination from '@/common/base/pagination';
import { SortOrder } from '@/common/base/head-sort-icon/types';
import { formatDateString } from '@/utils/dayjs';

interface ExportAnalyzeProps {
  initialState?: ExportAnalyzeType;
  mergeCompanyResponse: IApiCompanyCountryVO[];
  companyDetail: IApiEaseSourcingCompanyDetailResultVO;
}

const SectionWrapper = ({
  children,
  className,
  title,
}: {
  children: React.ReactNode;
  className?: string;
  title?: string;
}) => (
  <div className={`border relative border-[#E5E5E5] rounded-lg p-4 ${className || ''}`}>
    {title ? (
      <div className="  absolute l-5 t-5  text-[color:var(--neutral-900,#171717)] [font-family:Geist] text-xl font-semibold leading-[120%] tracking-[-0.4px]">
        {title}
      </div>
    ) : null}
    {children}
  </div>
);

const createLineChartOption = (data: IApiLineChartVO | undefined) => {
  // 折线图配置
  const lineChartOption = {
    backgroundColor: 'white',
    title: {
      text: 'Export trends',
      left: 'left',
      top: 4,
      textStyle: {
        fontFamily: 'Geist',
        fontWeight: 600,
        fontSize: 20,
        align: 'left',
        lineHeight: 1.2,
        color: '#171717',
      },
    },
    tooltip: {
      trigger: 'axis',
      formatter: function (params: any) {
        let result = params[0].name + '<br/>';
        params.forEach((item: any) => {
          result += item.marker + item.seriesName + ': ' + item.value + '<br/>';
        });
        return result;
      },
      backgroundColor: 'white',
      borderColor: '#E5E5E5',
      borderWidth: 1,
      shadowBlur: 4,
      shadowColor: 'rgba(0, 0, 0, 0.1)',
      shadowOffsetX: 0,
      shadowOffsetY: 2,
      padding: 10,
      textStyle: {
        color: '#262626',
        fontSize: 12,
      },
    },
    legend: {
      data: ['Number of exports', 'Total export amount'],
      right: 0,
      top: 0,
      textStyle: {
        color: '#737373',
        fontSize: 12,
      },
      icon: 'circle',
      itemWidth: 8,
      itemHeight: 8,
      itemGap: 20,
    },
    grid: {
      left: '3%',
      right: '4%',
      bottom: '15%',
      top: '20%',
      containLabel: true,
    },
    xAxis: {
      type: 'category',
      boundaryGap: true,
      data: data?.dateTimeList || [],
      axisLine: {
        lineStyle: {
          color: '#171717',
        },
      },
      axisLabel: {
        color: '#737373',
        fontSize: 12,
        rotate: 0,
      },
      axisTick: {
        show: false,
      },
    },
    yAxis: [
      {
        type: 'value',
        position: 'left',
        name: 'Number of exports',
        axisLine: {
          show: false,
        },
        minInterval: 1,
        axisLabel: {
          color: '#737373',
          fontSize: 12,
        },
        offset: 0,
        axisTick: {
          show: false,
        },
        nameLocation: 'end',
        nameTextStyle: {
          align: 'left',
        },
        splitLine: {
          show: false,
          lineStyle: {
            color: '#E5E5E5',
            type: 'dashed',
          },
        },
      },
      {
        type: 'value',
        position: 'right',
        name: 'Total export amount',
        nameTextStyle: {
          align: 'right',
        },
      },
    ],
    series: [
      {
        name: 'Number of exports',
        type: 'line',
        data: data?.countList || [],
        lineStyle: {
          color: '#37E4A6',
          width: 2,
        },
        itemStyle: {
          color: '#37E4A6',
          borderColor: 'white',
          borderWidth: 2,
        },
        symbol: 'circle',
        smooth: true,
        symbolSize: 6,
        yAxisIndex: 0,
      },
      {
        name: 'Total export amount',
        type: 'line',
        data: data?.priceList || [],
        yAxisIndex: 1,
        lineStyle: {
          color: '#171717',
          width: 2,
        },
        smooth: true,
        itemStyle: {
          color: '#171717',
          borderColor: 'white',
          borderWidth: 2,
        },
        symbol: 'circle',
        symbolSize: 6,
      },
    ],
  } satisfies echarts.EChartsOption;
  return lineChartOption;
};
interface DateRange {
  beginDate: string;
  endDate: string;
}
const pageSize = 6;

type RecordParams = Partial<
  Pick<IApiCompanyListRecordReq, 'from' | 'beginDate' | 'endDate' | 'goodsShipped' | 'sortBy' | 'order'>
>;
export const ExportAnalyzeComponent: React.FC<ExportAnalyzeProps> = props => {
  const { mergeCompanyResponse } = props;
  const [tabValue, setTabValue] = useState('overview');
  const [dateRange, setDateRange] = useState<DateRange | null>(null);
  const [recordDateRange, setRecordDateRange] = useState<DateRange | null>(null);
  const [recordInputValue, setRecordInputValue] = useState('');
  const [mapPageNo, setMapPageNo] = useState(1);
  // 排序字段
  const [sortBy, setSortBy] = useState<string>();
  // 排序顺序
  const [order, setOrder] = useState<SortOrder>('asc');

  useEffect(() => {
    // 获取默认时间范围
    getTimeLine({
      companyList: mergeCompanyResponse,
      recordType: 'export', //写死不用改
      sourceType: 'global', //写死不用改
    }).then(res => {
      setDateRange({
        beginDate: res.data.beginDate,
        endDate: res.data.endDate,
      });
      setRecordDateRange({
        beginDate: res.data.beginDate,
        endDate: res.data.endDate,
      });
    });
  }, [mergeCompanyResponse]);

  // / 导出统计
  const { data: exportStats } = useRequest(
    () => {
      if (!dateRange) return Promise.resolve(undefined);
      return getSuppliersBase({
        beginDate: dateRange.beginDate,
        endDate: dateRange.endDate,
        companyList: mergeCompanyResponse,
        sourceType: 'global', //写死不用改
      }).then(res => res.data);
    },
    {
      refreshDeps: [dateRange],
    }
  );
  /** 导出统计折线图数据 */
  const { data: exportDailyRes } = useRequest(
    () => {
      if (!dateRange) return Promise.resolve(undefined);
      return getSuppliersStatistics({
        beginDate: dateRange.beginDate,
        endDate: dateRange.endDate,

        companyList: mergeCompanyResponse.map(item => {
          return {
            ...item,
            recordType: 'export',
          };
        }),
        sourceType: 'global', //写死不用改
        mergedCompanyNames: [],
        selectedCompanyListV2: [],
        startYear: '',
        endYear: '',
      }).then(res => res.data);
    },
    {
      refreshDeps: [dateRange],
    }
  );

  // 买家地图列表统计接口
  const { data: exportStatisticsPage } = useRequest(
    () => {
      if (!dateRange) return Promise.resolve(undefined);
      return getBuyerMapList({
        size: 10,
        companyList: mergeCompanyResponse,
        order: 'desc',
        sourceType: 'global',
        mergedCompanyNames: [],
        selectedCompanyListV2: [],
        beginDate: dateRange.beginDate,
        endDate: dateRange.endDate,
        recordType: 'export',
        dimensionType: 1,
      }).then(res => res.data);
    },
    {
      refreshDeps: [dateRange],
    }
  );

  const latestPramasRef = useRef<IApiCompanyListRecordReq>();
  // 出口记录列表
  const { data: exportRecordList, run: runExportRecordList } = useRequest(
    (params: RecordParams) => {
      const newParams = {
        size: 10,
        companyList: mergeCompanyResponse,
        // country: 'Canada',
        // groupByCountry: true,
        from: params.from || 0,
        sortBy: 'shpmtDate',
        order: 'desc',
        hsCode: '',
        // goodsShipped: 'LED|LED|CONDUJO',
        exactlySearch: false,
        relationCountry: [],
        mergedCompanyNames: [],
        selectedCompanyListV2: [],
        beginDate: params.beginDate || '',
        endDate: params.endDate || '',
        sourceType: 'global',
        ...latestPramasRef.current,
        ...params,
      };
      latestPramasRef.current = newParams;
      return getSupplierTransctionRecordList(newParams).then(res => res.data);
    },
    {
      debounceLeading: true,
      debounceWait: 100,
      debounceTrailing: true,
      manual: true,
    }
  );
  useEffect(() => {
    if (recordDateRange) {
      runExportRecordList({
        // 0表示从第一页开始
        from: 0,
        ...(recordDateRange || ({} as IApiCompanyListRecordReq)),
        goodsShipped: recordInputValue,
      });
    }
  }, [runExportRecordList, recordDateRange, recordInputValue]);

  console.log('exportStats:', exportStats);
  console.log('exportDailyList:', exportDailyRes);
  console.log('exportStatisticsPage:', exportStatisticsPage);
  console.log('出口记录 exportRecordList:', exportRecordList);
  const lineChartOption = useMemo(() => {
    return createLineChartOption(exportDailyRes);
  }, [exportDailyRes]);

  const mapDataList = useMemo(() => {
    return (
      exportStatisticsPage?.areaStatVOList?.map(item => {
        return {
          name: item.countryCode,
          value: item.statVal,
        };
      }) || []
    );
  }, [exportStatisticsPage]);
  const areaStatVOList = useMemo(() => {
    const list = exportStatisticsPage?.areaStatVOList || [];
    // 默认 pageSize 为 6
    return list.slice((mapPageNo - 1) * pageSize, mapPageNo * pageSize);
  }, [exportStatisticsPage, mapPageNo]);

  return (
    <Tabs defaultValue={tabValue} className="w-full gap-5" onValueChange={setTabValue}>
      <div className="flex items-center justify-between">
        <TabsList className="grid grid-cols-2 w-80">
          <TabsTrigger value="overview" className=" cursor-pointer">
            Overview
          </TabsTrigger>
          <TabsTrigger value="record" className=" cursor-pointer">
            Record
          </TabsTrigger>
        </TabsList>
        {tabValue === 'overview' ? (
          <DateRangePicker
            value={{
              from: dateRange?.beginDate ? new Date(Date.parse(dateRange.beginDate)) : undefined,
              to: dateRange?.endDate ? new Date(Date.parse(dateRange.endDate)) : undefined,
            }}
            onValueChange={values => {
              setDateRange({
                beginDate: values?.from?.toISOString().split('T')[0] || '',
                endDate: values?.to?.toISOString().split('T')[0] || '',
              });
            }}
            showClearButton={false}
          />
        ) : null}
      </div>

      <TabsContent value="overview" className="space-y-5">
        <div className="grid grid-cols-3 gap-4">
          <StatMiniCard label="Export Count" icon={<Users color="#3B82F6" size={20} />} iconBgColor="#EFF6FF">
            {exportStats?.exportCount ?? '-'}
          </StatMiniCard>
          <StatMiniCard label="Export Value" icon={<DollarSign color="#10B981" size={20} />} iconBgColor="#ECFDF5">
            {exportStats?.exportOfUsd || '-'}
          </StatMiniCard>
          <StatMiniCard label="Last Export" icon={<Calendar color="#8B5CF6" size={20} />} iconBgColor="#F5F3FF">
            {formatDateString(exportStats?.lastExportTime) || '-'}
          </StatMiniCard>
        </div>
        <SectionWrapper className="h-100">
          <ReactECharts option={lineChartOption} style={{ height: '100%', width: '100%' }} />
        </SectionWrapper>
        {exportStatisticsPage?.areaStatVOList?.length ? (
          <SectionWrapper title="Buyers Map" className="flex justify-between">
            <div className="flex justify-center  items-center">
              <BuyersMapComponent mapData={mapDataList as any} />
            </div>
            <div className="pt-[30px]">
              <Table>
                <TableHeader>
                  <TableRow className="   hover:bg-none border-b-[1px] border-[#E5E5E5]">
                    <TableCell>No</TableCell>
                    <TableHead className={`w-3/6 `}>Country/Region</TableHead>
                    <TableHead className={`w-1/6`}>Transactions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {areaStatVOList.map((item, index) => (
                    <TableRow key={item.country} className="   hover:bg-none border-b-[1px] border-[#E5E5E5]">
                      <TableCell>{(mapPageNo - 1) * pageSize + index + 1}</TableCell>
                      <TableCell>{item.country}</TableCell>
                      <TableCell>{item.statVal}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <div className="flex justify-center mt-3">
                <BasePagination
                  page={mapPageNo}
                  pageSize={pageSize}
                  total={exportStatisticsPage?.areaStatVOList.length || 0}
                  onPageChange={page => {
                    setMapPageNo(page);
                  }}
                />
              </div>
            </div>
          </SectionWrapper>
        ) : null}
      </TabsContent>
      <TabsContent value="record" className="space-y-2">
        <div className="flex gap-3">
          <DateRangePicker
            value={{
              from: recordDateRange?.beginDate ? new Date(Date.parse(recordDateRange.beginDate)) : undefined,
              to: recordDateRange?.endDate ? new Date(Date.parse(recordDateRange.endDate)) : undefined,
            }}
            onValueChange={values => {
              setRecordDateRange({
                beginDate: values?.from?.toISOString().split('T')[0] || '',
                endDate: values?.to?.toISOString().split('T')[0] || '',
              });
            }}
            className="w-60!"
            showClearButton={false}
          />
          {/* 请输入产品描述 */}
          <Input
            placeholder=" Please input product description"
            className="w-114"
            value={recordInputValue}
            onChange={e => {
              setRecordInputValue(e.target.value);
            }}
          />
        </div>
        <RecordList
          data={exportRecordList}
          sortBy={sortBy}
          order={order}
          onSortChange={params => {
            setSortBy(params.sortBy);
            setOrder(params.order);
            runExportRecordList({
              sortBy: params.sortBy,
              order: params.order,
            });
          }}
          onPageChange={page => {
            runExportRecordList({
              from: page - 1,
            });
          }}
        />
      </TabsContent>
    </Tabs>
  );
};
